import { isNil } from 'lodash-es'
import { dateToFilterFormat } from '@/composables/useDateService'

const toDateOnlyString = (date: Date | null) => (date ? dateToFilterFormat(date) : null)

export const DeliveryDetails = (json: IDeliveryDetails): IDeliveryDetailsModel => {
	const {
		production_coverage,
		delivery_day,
		delivery_hours,
		products_count,
		delivery_coverage,
		order_number,
		document_path,
		complaint_id,
		document_name,
		invoice_number,
		...rest
	} = json

	return {
		...rest,
		deliveryCoverage: delivery_coverage,
		orderNumber: order_number,
		documentPath: document_path,
		complaintId: complaint_id,
		documentName: document_name,
		invoiceNumber: invoice_number ?? null,
		productionCoverage: production_coverage,
		deliveryDay: new Date(delivery_day),
		productsCount: products_count,
		deliveryHours: {
			hourFrom: delivery_hours.hour_from,
			hourTo: delivery_hours.hour_to,
		},
		// actions: true,
	}
}

const DeliveryProductBatch = (json: IDeliveryProductBatch, index: number): IDeliveryProductBatchModel => {
	const { batch_name, delivered_quantity, expiration_date, batch_number, complaint_reason, ...rest } = json

	return {
		...rest,
		batchName: batch_name,
		deliveredQty: delivered_quantity,
		expDate: expiration_date !== null ? new Date(expiration_date) : expiration_date,
		batchNo: batch_number,
		complaintReason: complaint_reason,
		id: index + 1,
	}
}

const isProductValid = (product: IDeliveryProductModel) => {
	if (product.batches.length) {
		return product.batches.every(el => {
			if (el.complaint) {
				return Boolean(el.deliveredQty)
			}
			return Boolean(el.deliveredQty) && Boolean(el.expDate) && Boolean(el.batchNo)
		})
	}
	return !isNil(product.deliveredQty) && Boolean(product.expDate) && Boolean(product.batchNo)
}

export const DeliveryProduct = (json: IDeliveryProduct): IDeliveryProductModel => {
	const {
		product_name,
		quantity,
		delivery_quantity,
		expiration_date,
		batch_number,
		batches,
		package_content_net,
		price_per_unit,
		supplier_quantity,
		supplier_unit,
		...rest
	} = json

	return {
		...rest,
		productName: product_name,
		orderedQty: quantity,
		deliveredQty: delivery_quantity,
		expDate: expiration_date ? new Date(expiration_date) : null,
		batchNo: batch_number,
		batches: batches?.length ? batches.map((el, index) => DeliveryProductBatch(el, index)) : [],
		packageContentNet: package_content_net,
		pricePerUnit: price_per_unit,
		supplierQuantity: supplier_quantity,
		supplierUnit: supplier_unit,
		actions: true,
		get valid() {
			return isProductValid(this)
		},
		get expander() {
			return Boolean(this.batches.length)
		},
	}
}

const DeliveryProductBatchToJson = (json: IDeliveryProductBatchModel): IDeliveryProductBatch => {
	const { batchName, deliveredQty, expDate, batchNo, complaintReason, complaint } = json

	return {
		batch_name: batchName,
		delivered_quantity: Number(deliveredQty),
		expiration_date: toDateOnlyString(expDate),
		batch_number: batchNo || '',
		complaint_reason: complaintReason,
		complaint,
	}
}

export const DeliveryProductToJson = (data: IDeliveryProductModel): IDeliveryProduct => {
	const {
		id,
		productName,
		orderedQty,
		unit,
		deliveredQty,
		expDate,
		batchNo,
		accepted,
		batches,
		packageContentNet,
		pricePerUnit,
		supplierQuantity,
		supplierUnit,
	} = data

	return {
		id,
		unit,
		accepted,
		product_name: productName,
		quantity: orderedQty,
		delivery_quantity: Number(deliveredQty) || null,
		expiration_date: toDateOnlyString(expDate),
		batch_number: batchNo || '',
		package_content_net: packageContentNet,
		price_per_unit: pricePerUnit,
		supplier_quantity: supplierQuantity,
		supplier_unit: supplierUnit,
		batches: batches?.length ? batches.map(el => DeliveryProductBatchToJson(el)) : [],
	}
}

export const ProductProblemToJson = (data: IProductProblemModel): IProductProblem => {
	const { deliveryDocumentNumber, ...rest } = data
	return {
		...rest,
		delivery_document_number: deliveryDocumentNumber,
	}
}

export const isDeliveryStartReceivingFormComplete = (
	values: IStartReceivingFormValues,
): values is ICompleteStartReceivingFormValues =>
	Boolean(values.deliveryDocumentNumber?.trim()) &&
	Boolean(values.deliveryDocumentDate) &&
	Boolean(values.receivedAt) &&
	values.transportTemperature !== undefined &&
	Boolean(values.vehicleCondition)

export const buildDeliveryStartReceivingFormPayload = (
	values: ICompleteStartReceivingFormValues,
): IWarehouseStartReceivingDeliveryFormPayload => ({
	deliveryDocumentNumber: values.deliveryDocumentNumber.trim(),
	comment: values.comment?.trim() || null,
	deliveryDocumentDate: values.deliveryDocumentDate,
	receivedAt: values.receivedAt,
	transportTemperature: values.transportTemperature,
	vehicleCondition: values.vehicleCondition,
})
